import { shallowEqual, type TypedUseSelectorHook, useDispatch as useAppDispatch, useSelector as useAppSelector } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit';
import type Context from '@alltrails/core/types/Context';
import context from '@alltrails/context/slices/contextSlice';
import { AlgoliaConfigs } from '@alltrails/types/algoliaConfigs';
import algoliaConfigs from '@alltrails/redux/slices/algoliaConfigs';
import lists, { type ListsState } from '@alltrails/redux/slices/lists';
import { MapState, mapReducer as map } from './reducer';
import { CustomRouteState, customRouteReducer as customRoute } from './customRoute';

type FullPreloadedState = Partial<{
  algoliaConfigs: AlgoliaConfigs;
  context: Context;
  lists: ListsState;
  map: MapState;
  customRoute: CustomRouteState;
}>;

type PartialPreloadedState = { [K in keyof FullPreloadedState]?: Partial<FullPreloadedState[K]> };
export type PreloadedState = PartialPreloadedState &
  Partial<{
    // If algoliaConfigs is provided it needs to be the full object
    algoliaConfigs: AlgoliaConfigs;
  }>;

export const reducer = {
  algoliaConfigs,
  context,
  lists,
  map,
  customRoute
};

export const buildStore = (preloadedState?: FullPreloadedState) => configureStore({ reducer, preloadedState });

export type AppStore = ReturnType<typeof buildStore>;
export type AppDispatch = AppStore['dispatch'];
export type RootState = ReturnType<AppStore['getState']>;

// these are intended to use within the package only
export const useDispatch = (): AppDispatch => useAppDispatch<AppDispatch>();
export const useSelector: TypedUseSelectorHook<RootState> = (selector: (state: RootState) => any, equalityFn = shallowEqual) =>
  useAppSelector<RootState, any>(selector, equalityFn);
